import { Application, Asset, Texture, Quat, ADDRESS_CLAMP_TO_EDGE, FILTER_LINEAR, PIXELFORMAT_R8_G8_B8_A8 } from 'playcanvas';
import type { SceneCubemapFaces, SceneSkyboxConfig } from '../config/manifest';

type CubemapSkyboxConfig = Extract<SceneSkyboxConfig, { type: 'cubemap' }>;
type VideoSkyboxConfig = Extract<SceneSkyboxConfig, { type: 'video' }>;

interface CubemapFaceSelection {
    faces: SceneCubemapFaces;
    variant: string;
}

function nowMs(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function toCubemapTextureUrls(faces: SceneCubemapFaces): string[] {
    return [faces.px, faces.nx, faces.py, faces.ny, faces.pz, faces.nz];
}

const _skyboxRotation = new Quat();

function normalizeSkyboxRotationYaw(rotationYaw: number | undefined): number {
    if (!Number.isFinite(rotationYaw)) return 0;
    return ((((rotationYaw as number) % 360) + 360) % 360);
}

function applySkyboxRotation(app: Application, rotationYaw: number | undefined): void {
    _skyboxRotation.setFromEulerAngles(0, normalizeSkyboxRotationYaw(rotationYaw), 0);
    app.scene.skyboxRotation = _skyboxRotation;
}

function selectCubemapFaces(config: CubemapSkyboxConfig): CubemapFaceSelection {
    return { faces: config.faces, variant: `yaw-${normalizeSkyboxRotationYaw(config.rotationYaw)}` };
}

function isUnsupportedDdsFallback(url: string, texture: Texture): boolean {
    const info = texture as Texture & { name?: string; width?: number; height?: number };
    return /\.dds$/i.test(url) &&
        info.name === 'dds-legacy-empty' &&
        info.width === 4 &&
        info.height === 4;
}

function formatTextureSize(texture: Texture | null | undefined): string {
    return texture instanceof Texture ? `${texture.width}x${texture.height}` : 'null';
}

class SkyboxSystem {
    private _app: Application;
    private _asset: Asset | null = null;
    private _videoElement: HTMLVideoElement | null = null;
    private _videoTexture: Texture | null = null;
    private _videoRafId: number | null = null;

    constructor(app: Application) {
        this._app = app;
    }

    /**
     * 加载并应用天空盒。
     *
     * @param options.keepCurrent 换装模式：不清当前天空盒——下载期间旧图继续
     *   显示，新图成功应用后才释放旧资产；失败/中途销毁则保留现状（不闪
     *   清屏色）。桌面端「手机档先顶上、全尺寸后台换装」用它。注意 video
     *   型新图的应用时机在 loadeddata 异步回调，走此模式时旧资产不做即时
     *   释放（留到下次 load/destroy，生产场景均为 cubemap）。
     */
    async load(config?: SceneSkyboxConfig, options?: { keepCurrent?: boolean }): Promise<void> {
        const previousAsset = this._asset;
        if (!options?.keepCurrent) {
            this.destroy();
            if (!config) return;
        } else if (!config) {
            return; // 无新配置：保持现状
        }

        if (config.type === 'cubemap') {
            await this.loadCubemapSkybox(config);
            this.releaseSupersededAsset(previousAsset);
            return;
        }
        if (config.type === 'video') {
            this.loadVideoSkybox(config);
            return;
        }
        await this.loadTextureSkybox(config);
        this.releaseSupersededAsset(previousAsset);
    }

    /** 换装成功后释放被顶掉的旧资产（scene 已不引用其纹理）。 */
    private releaseSupersededAsset(asset: Asset | null): void {
        if (!asset || asset === this._asset) return;
        this._app.assets.remove(asset);
        asset.unload();
    }

    private async loadTextureSkybox(config: Extract<SceneSkyboxConfig, { type: 'texture' }>): Promise<void> {
        const startedAt = nowMs();
        let lastProgressLogMs = Number.NEGATIVE_INFINITY;
        console.info(`[skybox] texture:start url=${config.url}`);
        const asset = new Asset(`skybox:${config.url}`, 'texture', { url: config.url });
        this._asset = asset;
        this._app.assets.add(asset);

        const texture = await new Promise<Texture>((resolve, reject) => {
            asset.on('progress', (received: number, total: number) => {
                const progressAt = nowMs();
                if (progressAt - lastProgressLogMs < 1000) return;
                lastProgressLogMs = progressAt;
                console.info(
                    `[skybox] texture:progress url=${config.url}` +
                    ` received=${Math.round(received ?? 0)} total=${Math.round(total ?? 0)}` +
                    ` elapsedMs=${Math.round(progressAt - startedAt)}`
                );
            });
            asset.once('load', () => {
                console.info(`[skybox] texture:asset-loaded url=${config.url} elapsedMs=${Math.round(nowMs() - startedAt)}`);
                const resource = asset.resource;
                if (resource instanceof Texture) resolve(resource);
                else reject(new Error(`Skybox asset did not load as a texture: ${config.url}`));
            });
            asset.once('error', (err: unknown) => {
                console.warn(`[skybox] texture:error url=${config.url} elapsedMs=${Math.round(nowMs() - startedAt)}`, err);
                reject(new Error(String(err)));
            });
            this._app.assets.load(asset);
        });

        if (this._asset !== asset) return;
        if (isUnsupportedDdsFallback(config.url, texture)) {
            this.destroy();
            throw new Error(`Unsupported DDS skybox texture: ${config.url}. Convert it to a PlayCanvas-supported legacy DDS before using it as a skybox.`);
        }
        this._app.scene.skybox = texture;
        this._app.scene.skyboxIntensity = config.intensity ?? 1;
        applySkyboxRotation(this._app, 0);
        console.info(
            `[skybox] texture:applied url=${config.url}` +
            ` size=${texture.width}x${texture.height} totalMs=${Math.round(nowMs() - startedAt)}`
        );
    }

    private async loadCubemapSkybox(config: CubemapSkyboxConfig): Promise<void> {
        const startedAt = nowMs();
        let lastProgressLogMs = Number.NEGATIVE_INFINITY;
        const selection = selectCubemapFaces(config);
        const urls = toCubemapTextureUrls(selection.faces);
        const name = Object.values(selection.faces).join('|');
        console.info(`[skybox] cubemap:start variant=${selection.variant} faces=${urls.length} urls=${urls.join(',')}`);
        const asset = new Asset(`skybox:${name}`, 'cubemap', undefined, {
            textures: urls,
            mipmaps: false
        });
        this._asset = asset;
        this._app.assets.add(asset);

        const resources = await new Promise<Texture[]>((resolve, reject) => {
            asset.on('progress', (received: number, total: number) => {
                const progressAt = nowMs();
                if (progressAt - lastProgressLogMs < 1000) return;
                lastProgressLogMs = progressAt;
                console.info(
                    `[skybox] cubemap:progress variant=${selection.variant} faces=${urls.length}` +
                    ` received=${Math.round(received ?? 0)} total=${Math.round(total ?? 0)}` +
                    ` elapsedMs=${Math.round(progressAt - startedAt)}`
                );
            });
            asset.once('load', () => {
                console.info(`[skybox] cubemap:asset-loaded variant=${selection.variant} faces=${urls.length} elapsedMs=${Math.round(nowMs() - startedAt)}`);
                const loaded = asset.resources as Texture[];
                if (loaded?.[0] instanceof Texture) resolve(loaded);
                else reject(new Error(`Skybox cubemap asset did not load: ${name}`));
            });
            asset.once('error', (err: unknown) => {
                console.warn(`[skybox] cubemap:error variant=${selection.variant} faces=${urls.length} elapsedMs=${Math.round(nowMs() - startedAt)}`, err);
                reject(new Error(String(err)));
            });
            this._app.assets.load(asset);
        });

        if (this._asset !== asset) return;
        this._app.scene.setSkybox(resources);
        this._app.scene.skyboxIntensity = config.intensity ?? 1;
        applySkyboxRotation(this._app, config.rotationYaw);
        console.info(
            `[skybox] cubemap:applied variant=${selection.variant} faces=${resources.length}` +
            ` sizes=${resources.map(formatTextureSize).join(',')}` +
            ` totalMs=${Math.round(nowMs() - startedAt)}`
        );
    }

    /**
     * Load a panoramic (equirect 360°) video as the skybox.  Creates a
     * hidden <video> element, a PlayCanvas Texture backed by it, and
     * continuously uploads video frames to the texture via rAF.  The
     * texture is assigned to scene.skybox (single-texture skybox mode).
     */
    private loadVideoSkybox(config: VideoSkyboxConfig): void {
        const video = document.createElement('video');
        video.src = config.url;
        video.muted = true;
        video.loop = config.loop ?? true;
        video.playsInline = true;
        video.crossOrigin = 'anonymous';
        video.autoplay = config.autoplay ?? true;
        video.style.display = 'none';
        document.body.appendChild(video);
        this._videoElement = video;

        const texture = new Texture(this._app.graphicsDevice, {
            name: `skybox-video:${config.url}`,
            width: 2,
            height: 1,
            format: PIXELFORMAT_R8_G8_B8_A8,
            mipmaps: false,
            minFilter: FILTER_LINEAR,
            magFilter: FILTER_LINEAR,
            addressU: ADDRESS_CLAMP_TO_EDGE,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });
        this._videoTexture = texture;

        const applyTexture = () => {
            texture.setSource(video);
            this._app.scene.skybox = texture;
            this._app.scene.skyboxIntensity = config.intensity ?? 1;
            applySkyboxRotation(this._app, 0);
            console.info(`[skybox] video:applied url=${config.url}`);
        };

        const pump = () => {
            if (!this._videoTexture) return;
            texture.setSource(video);
            texture.upload();
            this._videoRafId = requestAnimationFrame(pump);
        };

        video.addEventListener('loadeddata', () => {
            applyTexture();
            pump();
        }, { once: true });

        video.addEventListener('error', (e) => {
            console.warn(`[skybox] video:error url=${config.url}`, e);
        });

        // Attempt to start playback; muted autoplay is generally allowed.
        video.play().catch((err) => {
            console.warn(`[skybox] video:playback blocked url=${config.url}`, err);
        });
    }

    destroy(): void {
        if (this._videoRafId !== null) {
            cancelAnimationFrame(this._videoRafId);
            this._videoRafId = null;
        }
        if (this._videoTexture) {
            this._videoTexture.destroy();
            this._videoTexture = null;
        }
        if (this._videoElement) {
            this._videoElement.pause();
            this._videoElement.remove();
            this._videoElement = null;
        }
        this._app.scene.setSkybox();
        this._app.scene.skybox = null;
        applySkyboxRotation(this._app, 0);
        if (this._asset) {
            this._app.assets.remove(this._asset);
            this._asset.unload();
            this._asset = null;
        }
    }
}

export { SkyboxSystem };
